import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { ROLE_LABELS, ROLE_BADGE, formatDate } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { UserPlus, Trash2, Pencil, Search, ChevronLeft, ChevronRight, Users2, Crown, ShieldCheck } from "lucide-react";

const th = "h-8 px-1.5 text-[11px] font-semibold whitespace-nowrap";
const td = "px-1.5 py-1.5 text-xs";
const PAGE_SIZE = 8;
const MASK = "••••••••";

const emptyCreate = { username: "", name: "", password: "", role: "karyawan", email: "", task_note: "", phone: "", address: "", can_disburse_cashback: false, can_manage_billing: false, can_pay_supplier: false, can_view_cash_bank: false, can_delete_data: false, can_approve_deletion: false, can_view_all_data: false, can_full_access: false, can_create_order: false, security_question: "", security_answer: "" };

function UserCard({ u, isStaff, canManage, onEdit, onToggle, onDelete }) {
  return (
    <div data-testid={`user-row-${u.username}`} className="p-3 space-y-1.5">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-semibold">{u.name}</p>
          <p className="text-[11px] text-muted-foreground">@{u.username} · {MASK}</p>
        </div>
        <Badge variant="outline" className={`${ROLE_BADGE[u.role]} text-[10px] px-1.5 py-0 shrink-0`}>{ROLE_LABELS[u.role]}</Badge>
      </div>
      <div className="space-y-0.5 text-[11px]">
        <div className="flex justify-between gap-3"><span className="text-muted-foreground shrink-0">No. HP</span><span className="text-right break-all">{u.phone || "-"}</span></div>
        <div className="flex justify-between gap-3"><span className="text-muted-foreground shrink-0">Alamat</span><span className="text-right break-words min-w-0">{u.address || "-"}</span></div>
        {isStaff && u.task_note && <div className="flex justify-between gap-3"><span className="text-muted-foreground shrink-0">Tugas</span><span className="text-right break-words min-w-0">{u.task_note}</span></div>}
        <div className="flex justify-between gap-3"><span className="text-muted-foreground">Status</span><span className={u.active ? "text-emerald-600 font-medium" : "text-rose-600 font-medium"}>{u.active ? "Aktif" : "Nonaktif"}</span></div>
        <div className="flex justify-between gap-3"><span className="text-muted-foreground">Dibuat</span><span>{formatDate(u.created_at)}</span></div>
      </div>
      <div className="flex justify-end gap-1 pt-0.5">
        {canManage && (
          <Button variant="outline" size="sm" className="h-7 text-xs gap-1 px-2" onClick={() => onEdit(u)} data-testid={`user-edit-${u.username}`}>
            <Pencil className="h-3 w-3" /> Edit
          </Button>
        )}
        {canManage && (
          <div className="flex items-center gap-1 px-1">
            <span className="text-[11px] text-muted-foreground">Aktif</span>
            <Switch checked={u.active} onCheckedChange={() => onToggle(u)} disabled={u.role === "owner"} data-testid={`user-active-${u.username}`} />
          </div>
        )}
        {canManage && u.role !== "owner" && (
          <Button variant="ghost" size="sm" className="h-7 text-xs gap-1 px-2 text-rose-600 hover:text-rose-700" onClick={() => onDelete(u._id)} data-testid={`user-delete-${u.username}`}>
            <Trash2 className="h-3 w-3" /> Hapus
          </Button>
        )}
      </div>
    </div>
  );
}

export function UserGroup({ title, icon: Icon, rows, isStaff, canManage, groupId, onEdit, onToggle, onDelete, onAdd, addLabel }) {
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const filtered = rows.filter((u) => {
    const s = q.toLowerCase();
    return !s || u.name.toLowerCase().includes(s) || u.username.toLowerCase().includes(s) || (u.phone || "").includes(s);
  });
  const pages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const cur = Math.min(page, pages);
  const slice = filtered.slice((cur - 1) * PAGE_SIZE, cur * PAGE_SIZE);

  return (
    <Card className="overflow-hidden" data-testid={`users-group-${groupId}`}>
      <div className="p-3 border-b border-border flex flex-col sm:flex-row sm:items-center gap-2 justify-between bg-secondary/40">
        <div className="flex items-center gap-2">
          <div className="grid place-items-center h-8 w-8 rounded-lg bg-primary/10 shrink-0"><Icon className="h-4 w-4 text-primary" /></div>
          <div>
            <p className="font-heading text-sm font-bold leading-tight">{title}</p>
            <p className="text-[11px] text-muted-foreground">{filtered.length} akun</p>
          </div>
        </div>
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <div className="relative flex-1 sm:w-56">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input data-testid={`users-search-${groupId}`} value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="Cari nama / username / HP" className="pl-8 h-8 text-xs" />
          </div>
          {onAdd && (
            <Button data-testid={`users-add-${groupId}`} size="sm" className="gap-1.5 h-8 text-xs shrink-0" onClick={onAdd}>
              <UserPlus className="h-3.5 w-3.5" /> {addLabel}
            </Button>
          )}
        </div>
      </div>

      <div className="hidden lg:block">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className={th}>Nama</TableHead>
              <TableHead className={th}>Username</TableHead>
              <TableHead className={th}>Password</TableHead>
              <TableHead className={th}>No. HP</TableHead>
              <TableHead className={th}>Alamat</TableHead>
              {isStaff && <TableHead className={th}>Role / Hak Akses</TableHead>}
              <TableHead className={th}>Status</TableHead>
              <TableHead className={`${th} text-right`}>Aksi</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {slice.length === 0 && <TableRow><TableCell colSpan={isStaff ? 8 : 7} className="text-center text-muted-foreground text-xs py-8">Tidak ada akun.</TableCell></TableRow>}
            {slice.map((u) => (
              <TableRow key={u._id} data-testid={`user-row-${u.username}`}>
                <TableCell className={`${td} font-medium whitespace-nowrap`}>{u.name}</TableCell>
                <TableCell className={`${td} text-muted-foreground`}>{u.username}</TableCell>
                <TableCell className={`${td} text-muted-foreground tracking-widest`}>{MASK}</TableCell>
                <TableCell className={`${td} whitespace-nowrap`}>{u.phone || "-"}</TableCell>
                <TableCell className={`${td} text-muted-foreground max-w-[180px] whitespace-normal break-words`}>{u.address || "-"}</TableCell>
                {isStaff && (
                  <TableCell className={td}>
                    <Badge variant="outline" className={`${ROLE_BADGE[u.role]} text-[10px] px-1.5 py-0`}>{ROLE_LABELS[u.role]}</Badge>
                    {u.role === "admin" && u.can_disburse_cashback && <span className="block text-[10px] text-amber-600 mt-0.5">+ Izin Cashback</span>}
                    {u.role === "karyawan" && u.task_note && <span className="block text-[10px] text-muted-foreground mt-0.5">{u.task_note}</span>}
                  </TableCell>
                )}
                <TableCell className={td}>
                  <div className="flex items-center gap-1.5">
                    <Switch checked={u.active} onCheckedChange={() => onToggle(u)} disabled={!canManage || u.role === "owner"} data-testid={`user-active-${u.username}`} />
                    <span className={`text-[10px] ${u.active ? "text-emerald-600" : "text-rose-600"}`}>{u.active ? "Aktif" : "Nonaktif"}</span>
                  </div>
                </TableCell>
                <TableCell className={`${td} text-right`}>
                  {canManage && (
                    <div className="inline-flex gap-0.5 justify-end">
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-primary" onClick={() => onEdit(u)} data-testid={`user-edit-${u.username}`} title="Edit">
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      {u.role !== "owner" && (
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-destructive" onClick={() => onDelete(u._id)} data-testid={`user-delete-${u.username}`} title="Hapus">
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <div className="lg:hidden divide-y divide-border">
        {slice.length === 0 && <p className="text-center text-muted-foreground text-xs py-8">Tidak ada akun.</p>}
        {slice.map((u) => <UserCard key={u._id} u={u} isStaff={isStaff} canManage={canManage} onEdit={onEdit} onToggle={onToggle} onDelete={onDelete} />)}
      </div>

      {pages > 1 && (
        <div className="p-2.5 border-t border-border flex items-center justify-between text-xs">
          <span className="text-muted-foreground">Halaman {cur} dari {pages}</span>
          <div className="flex gap-1">
            <Button variant="outline" size="sm" className="h-7 w-7 p-0" disabled={cur <= 1} onClick={() => setPage(cur - 1)} data-testid={`users-prev-${groupId}`}><ChevronLeft className="h-3.5 w-3.5" /></Button>
            <Button variant="outline" size="sm" className="h-7 w-7 p-0" disabled={cur >= pages} onClick={() => setPage(cur + 1)} data-testid={`users-next-${groupId}`}><ChevronRight className="h-3.5 w-3.5" /></Button>
          </div>
        </div>
      )}
    </Card>
  );
}

export default function Users() {
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyCreate);
  const [editFor, setEditFor] = useState(null);
  const [eform, setEform] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/users").then((r) => setUsers(r.data));
  useEffect(() => { load(); }, []);

  const roleOptions = ["admin", "karyawan"];
  const byRole = (r) => users.filter((u) => u.role === r);
  const openCreate = (role) => { setForm({ ...emptyCreate, role }); setOpen(true); };

  const submit = async () => {
    if (!form.username || !form.name || !form.password) { toast.error("Lengkapi username, nama, dan password"); return; }
    if (form.password.length < 6) { toast.error("Password minimal 6 karakter"); return; }
    setBusy(true);
    try {
      await api.post("/users", form);
      toast.success("Pengguna berhasil dibuat");
      setOpen(false); setForm(emptyCreate); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const openEdit = (u) => {
    setEditFor(u);
    setEform({
      username: u.username, name: u.name, password: "", phone: u.phone || "", address: u.address || "",
      email: u.email || "", task_note: u.task_note || "", role: u.role, security_question: u.security_question || "", security_answer: "",
      active: u.active, can_disburse_cashback: !!u.can_disburse_cashback, can_manage_billing: !!u.can_manage_billing, can_pay_supplier: !!u.can_pay_supplier, can_view_cash_bank: !!u.can_view_cash_bank, can_delete_data: !!u.can_delete_data, can_approve_deletion: !!u.can_approve_deletion, can_view_all_data: !!u.can_view_all_data, can_full_access: !!u.can_full_access, can_create_order: !!u.can_create_order,
    });
  };

  const submitEdit = async () => {
    if (!eform.name.trim() || !eform.username.trim()) { toast.error("Nama dan username wajib diisi"); return; }
    if (eform.password && eform.password.length < 6) { toast.error("Password minimal 6 karakter"); return; }
    setBusy(true);
    try {
      const body = {
        username: eform.username.trim(), name: eform.name.trim(), phone: eform.phone, address: eform.address,
        email: eform.email, task_note: eform.task_note, active: eform.active,
        security_question: eform.security_question,
      };
      if (eform.security_answer) body.security_answer = eform.security_answer;
      if (eform.password) body.password = eform.password;
      if (editFor.role !== "owner") {
        body.role = eform.role;
        body.can_disburse_cashback = eform.can_disburse_cashback;
        body.can_manage_billing = eform.can_manage_billing;
        body.can_pay_supplier = eform.can_pay_supplier;
        body.can_view_cash_bank = eform.can_view_cash_bank;
        body.can_delete_data = eform.can_delete_data;
        body.can_approve_deletion = eform.can_approve_deletion;
        body.can_view_all_data = eform.can_view_all_data;
        body.can_full_access = eform.can_full_access;
        body.can_create_order = eform.can_create_order;
      }
      await api.patch(`/users/${editFor._id}`, body);
      toast.success("Akun diperbarui");
      setEditFor(null); setEform(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const toggleActive = async (u) => {
    try { await api.patch(`/users/${u._id}`, { active: !u.active }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (id) => {
    try {
      const res = await api.post("/deletion-requests", { resource_type: "user", resource_id: id, label: "akun pengguna" });
      toast.success(res.data.pending ? "Permintaan hapus dikirim, menunggu persetujuan Owner" : "Pengguna dihapus");
      load();
    }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2.5">
        <div>
          <h2 className="font-heading text-lg sm:text-xl font-bold tracking-tight">Manajemen Pengguna</h2>
          <p className="text-xs sm:text-sm text-muted-foreground">Akun Owner, Admin, dan Karyawan dikelola pada daftar terpisah. Akun Supplier dikelola di menu Data Supplier.</p>
        </div>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setForm(emptyCreate); }}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading text-base">Buat Pengguna Baru</DialogTitle>
              <DialogDescription className="text-xs">Password disimpan aman (hashed) dan tidak ditampilkan.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-1">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label className="text-xs">Username</Label><Input data-testid="user-username" className="h-9 text-xs" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} placeholder="cth. budi" /></div>
                <div className="space-y-1"><Label className="text-xs">Nama Lengkap</Label><Input data-testid="user-name" className="h-9 text-xs" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              </div>
              <div className="space-y-1"><Label className="text-xs">Password</Label><Input data-testid="user-password" type="text" className="h-9 text-xs" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Min. 6 karakter" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Role</Label>
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                    <SelectTrigger data-testid="user-role" className="h-9 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>{roleOptions.map((r) => <SelectItem key={r} value={r} className="text-xs">{ROLE_LABELS[r]}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1"><Label className="text-xs">No. HP</Label><Input data-testid="user-phone" className="h-9 text-xs" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="08xx..." /></div>
              </div>
              <div className="space-y-1"><Label className="text-xs">Alamat</Label><Input data-testid="user-address" className="h-9 text-xs" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
              <div className="space-y-1"><Label className="text-xs">Email (opsional)</Label><Input data-testid="user-email" className="h-9 text-xs" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
              <div className="rounded-lg border border-dashed border-border p-2.5 space-y-2">
                <p className="text-[11px] font-medium text-muted-foreground">Pertanyaan Keamanan (untuk fitur Lupa Password)</p>
                <div className="space-y-1"><Label className="text-xs">Pertanyaan</Label><Input data-testid="user-sec-question" className="h-9 text-xs" value={form.security_question} onChange={(e) => setForm({ ...form, security_question: e.target.value })} placeholder="cth. Nama hewan peliharaan pertama?" /></div>
                <div className="space-y-1"><Label className="text-xs">Jawaban</Label><Input data-testid="user-sec-answer" className="h-9 text-xs" value={form.security_answer} onChange={(e) => setForm({ ...form, security_answer: e.target.value })} placeholder="Jawaban rahasia" /></div>
              </div>
              {form.role === "karyawan" && (
                <div className="space-y-1"><Label className="text-xs">Tugas / Batasan</Label><Input data-testid="user-task" className="h-9 text-xs" value={form.task_note} onChange={(e) => setForm({ ...form, task_note: e.target.value })} placeholder="cth. Input penjualan shift pagi" /></div>
              )}
              {form.role === "admin" && (
                <div className="flex items-center justify-between rounded-lg border border-border p-2.5">
                  <Label className="text-xs">Izin Pencairan Cashback</Label>
                  <Switch checked={form.can_disburse_cashback} onCheckedChange={(v) => setForm({ ...form, can_disburse_cashback: v })} data-testid="user-cashback-perm" />
                </div>
              )}
              {form.role === "karyawan" && (
                <div className="flex items-center justify-between rounded-lg border border-amber-300 bg-amber-50/70 dark:border-amber-700 dark:bg-amber-950/20 p-2.5">
                  <div>
                    <Label className="text-xs font-semibold">Izin Akses Penuh (seperti Owner)</Label>
                    <p className="text-[10px] text-muted-foreground">Karyawan dapat mengakses semua menu & data seperti Owner.</p>
                  </div>
                  <Switch checked={form.can_full_access} onCheckedChange={(v) => setForm({ ...form, can_full_access: v })} data-testid="user-full-access-perm" />
                </div>
              )}
              {form.role === "karyawan" && (
                <div className="flex items-center justify-between rounded-lg border border-border p-2.5">
                  <div>
                    <Label className="text-xs">Izin Membuat Order</Label>
                    <p className="text-[10px] text-muted-foreground">Default karyawan hanya Absensi. Aktifkan agar bisa buat/isi order.</p>
                  </div>
                  <Switch checked={form.can_create_order} onCheckedChange={(v) => setForm({ ...form, can_create_order: v })} data-testid="user-create-order-perm" />
                </div>
              )}
              {(form.role === "admin" || form.role === "karyawan") && (
                <div className="flex items-center justify-between rounded-lg border border-border p-2.5">
                  <Label className="text-xs">Izin Tagihan Pelanggan</Label>
                  <Switch checked={form.can_manage_billing} onCheckedChange={(v) => setForm({ ...form, can_manage_billing: v })} data-testid="user-billing-perm" />
                </div>
              )}
              {form.role === "admin" && (
                <div className="flex items-center justify-between rounded-lg border border-border p-2.5">
                  <Label className="text-xs">Izin Pembayaran Supplier</Label>
                  <Switch checked={form.can_pay_supplier} onCheckedChange={(v) => setForm({ ...form, can_pay_supplier: v })} data-testid="user-supplier-pay-perm" />
                </div>
              )}
              {form.role === "admin" && (
                <div className="flex items-center justify-between rounded-lg border border-border p-2.5">
                  <Label className="text-xs">Izin Kas & Bank</Label>
                  <Switch checked={form.can_view_cash_bank} onCheckedChange={(v) => setForm({ ...form, can_view_cash_bank: v })} data-testid="user-cash-bank-perm" />
                </div>
              )}
              {form.role === "admin" && (
                <div className="flex items-center justify-between rounded-lg border border-border p-2.5">
                  <Label className="text-xs">Izin Hapus Data</Label>
                  <Switch checked={form.can_delete_data} onCheckedChange={(v) => setForm({ ...form, can_delete_data: v })} data-testid="user-delete-data-perm" />
                </div>
              )}
              {form.role === "admin" && (
                <div className="flex items-center justify-between rounded-lg border border-border p-2.5">
                  <Label className="text-xs">Izin Setujui Hapus</Label>
                  <Switch checked={form.can_approve_deletion} onCheckedChange={(v) => setForm({ ...form, can_approve_deletion: v })} data-testid="user-approve-del-perm" />
                </div>
              )}
              {form.role === "admin" && (
                <div className="flex items-center justify-between rounded-lg border border-border p-2.5">
                  <Label className="text-xs">Lihat Semua Data Admin</Label>
                  <Switch checked={form.can_view_all_data} onCheckedChange={(v) => setForm({ ...form, can_view_all_data: v })} data-testid="user-view-all-perm" />
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => setOpen(false)}>Batal</Button>
              <Button onClick={submit} disabled={busy} data-testid="user-submit" variant="gold" size="sm" className="h-8 text-xs">Simpan</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <UserGroup title="Owner" icon={Crown} rows={byRole("owner")} isStaff canManage groupId="owner"
        onEdit={openEdit} onToggle={toggleActive} onDelete={remove} />
      <UserGroup title="Admin" icon={ShieldCheck} rows={byRole("admin")} isStaff canManage groupId="admin"
        onEdit={openEdit} onToggle={toggleActive} onDelete={remove}
        onAdd={() => openCreate("admin")} addLabel="Tambah Admin" />
      <UserGroup title="Karyawan" icon={Users2} rows={byRole("karyawan")} isStaff canManage groupId="karyawan"
        onEdit={openEdit} onToggle={toggleActive} onDelete={remove}
        onAdd={() => openCreate("karyawan")} addLabel="Tambah Karyawan" />

      <Dialog open={!!editFor} onOpenChange={(o) => !o && setEditFor(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-base">Edit Akun {editFor ? `— ${editFor.name}` : ""}</DialogTitle>
            <DialogDescription className="text-xs">Kosongkan kolom password bila tidak ingin mengubahnya.</DialogDescription>
          </DialogHeader>
          {editFor && eform && (
            <div className="space-y-3 py-1">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label className="text-xs">Nama</Label><Input data-testid="user-edit-name" className="h-9 text-xs" value={eform.name} onChange={(e) => setEform({ ...eform, name: e.target.value })} /></div>
                <div className="space-y-1"><Label className="text-xs">Username</Label><Input data-testid="user-edit-username" className="h-9 text-xs" value={eform.username} onChange={(e) => setEform({ ...eform, username: e.target.value })} /></div>
              </div>
              <div className="space-y-1"><Label className="text-xs">Password Baru (opsional)</Label><Input data-testid="user-edit-password" type="text" className="h-9 text-xs" value={eform.password} onChange={(e) => setEform({ ...eform, password: e.target.value })} placeholder="Biarkan kosong = password lama tetap" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label className="text-xs">No. HP</Label><Input data-testid="user-edit-phone" className="h-9 text-xs" value={eform.phone} onChange={(e) => setEform({ ...eform, phone: e.target.value })} /></div>
                <div className="space-y-1"><Label className="text-xs">Email</Label><Input data-testid="user-edit-email" className="h-9 text-xs" value={eform.email} onChange={(e) => setEform({ ...eform, email: e.target.value })} /></div>
              </div>
              <div className="space-y-1"><Label className="text-xs">Alamat</Label><Input data-testid="user-edit-address" className="h-9 text-xs" value={eform.address} onChange={(e) => setEform({ ...eform, address: e.target.value })} /></div>
              <div className="rounded-lg border border-dashed border-border p-2.5 space-y-2">
                <p className="text-[11px] font-medium text-muted-foreground">Pertanyaan Keamanan {eform.security_question ? "(sudah diatur)" : "(belum diatur)"}</p>
                <div className="space-y-1"><Label className="text-xs">Pertanyaan</Label><Input data-testid="user-edit-sec-question" className="h-9 text-xs" value={eform.security_question} onChange={(e) => setEform({ ...eform, security_question: e.target.value })} placeholder="cth. Nama hewan peliharaan pertama?" /></div>
                <div className="space-y-1"><Label className="text-xs">Jawaban Baru (opsional)</Label><Input data-testid="user-edit-sec-answer" className="h-9 text-xs" value={eform.security_answer} onChange={(e) => setEform({ ...eform, security_answer: e.target.value })} placeholder="Kosongkan = jawaban lama tetap" /></div>
              </div>
              {editFor.role !== "owner" && (
                <>
                  <div className="space-y-1">
                    <Label className="text-xs">Role</Label>
                    <Select value={eform.role} onValueChange={(v) => setEform({ ...eform, role: v })}>
                      <SelectTrigger data-testid="user-edit-role" className="h-9 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>{roleOptions.map((r) => <SelectItem key={r} value={r} className="text-xs">{ROLE_LABELS[r]}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  {eform.role === "karyawan" && (
                    <div className="space-y-1"><Label className="text-xs">Tugas / Batasan</Label><Input data-testid="user-edit-task" className="h-9 text-xs" value={eform.task_note} onChange={(e) => setEform({ ...eform, task_note: e.target.value })} /></div>
                  )}
                  <div className="grid grid-cols-2 gap-3">
                    {eform.role === "admin" && (
                      <div className="flex items-center justify-between rounded-lg border border-border p-2.5">
                        <Label className="text-xs">Izin Pencairan Cashback</Label>
                        <Switch checked={eform.can_disburse_cashback} onCheckedChange={(v) => setEform({ ...eform, can_disburse_cashback: v })} data-testid="user-edit-cashback-perm" />
                      </div>
                    )}
                    {eform.role === "karyawan" && (
                      <div className="flex items-center justify-between rounded-lg border border-amber-300 bg-amber-50/70 dark:border-amber-700 dark:bg-amber-950/20 p-2.5 col-span-2">
                        <div>
                          <Label className="text-xs font-semibold">Izin Akses Penuh (seperti Owner)</Label>
                          <p className="text-[10px] text-muted-foreground">Karyawan dapat mengakses semua menu & data seperti Owner.</p>
                        </div>
                        <Switch checked={eform.can_full_access} onCheckedChange={(v) => setEform({ ...eform, can_full_access: v })} data-testid="user-edit-full-access-perm" />
                      </div>
                    )}
                    {eform.role === "karyawan" && (
                      <div className="flex items-center justify-between rounded-lg border border-border p-2.5 col-span-2">
                        <div>
                          <Label className="text-xs">Izin Membuat Order</Label>
                          <p className="text-[10px] text-muted-foreground">Default karyawan hanya Absensi. Aktifkan agar bisa buat/isi order.</p>
                        </div>
                        <Switch checked={eform.can_create_order} onCheckedChange={(v) => setEform({ ...eform, can_create_order: v })} data-testid="user-edit-create-order-perm" />
                      </div>
                    )}
                    {(eform.role === "admin" || eform.role === "karyawan") && (
                      <div className="flex items-center justify-between rounded-lg border border-border p-2.5">
                        <Label className="text-xs">Izin Tagihan Pelanggan</Label>
                        <Switch checked={eform.can_manage_billing} onCheckedChange={(v) => setEform({ ...eform, can_manage_billing: v })} data-testid="user-edit-billing-perm" />
                      </div>
                    )}
                    {eform.role === "admin" && (
                      <div className="flex items-center justify-between rounded-lg border border-border p-2.5">
                        <Label className="text-xs">Izin Pembayaran Supplier</Label>
                        <Switch checked={eform.can_pay_supplier} onCheckedChange={(v) => setEform({ ...eform, can_pay_supplier: v })} data-testid="user-edit-supplier-pay-perm" />
                      </div>
                    )}
                    {eform.role === "admin" && (
                      <div className="flex items-center justify-between rounded-lg border border-border p-2.5">
                        <Label className="text-xs">Izin Kas & Bank</Label>
                        <Switch checked={eform.can_view_cash_bank} onCheckedChange={(v) => setEform({ ...eform, can_view_cash_bank: v })} data-testid="user-edit-cash-bank-perm" />
                      </div>
                    )}
                    {eform.role === "admin" && (
                      <div className="flex items-center justify-between rounded-lg border border-border p-2.5">
                        <Label className="text-xs">Izin Hapus Data</Label>
                        <Switch checked={eform.can_delete_data} onCheckedChange={(v) => setEform({ ...eform, can_delete_data: v })} data-testid="user-edit-delete-data-perm" />
                      </div>
                    )}
                    {eform.role === "admin" && (
                      <div className="flex items-center justify-between rounded-lg border border-border p-2.5">
                        <Label className="text-xs">Izin Setujui Hapus</Label>
                        <Switch checked={eform.can_approve_deletion} onCheckedChange={(v) => setEform({ ...eform, can_approve_deletion: v })} data-testid="user-edit-approve-del-perm" />
                      </div>
                    )}
                    {eform.role === "admin" && (
                      <div className="flex items-center justify-between rounded-lg border border-border p-2.5">
                        <Label className="text-xs">Lihat Semua Data Admin</Label>
                        <Switch checked={eform.can_view_all_data} onCheckedChange={(v) => setEform({ ...eform, can_view_all_data: v })} data-testid="user-edit-view-all-perm" />
                      </div>
                    )}
                    <div className="flex items-center justify-between rounded-lg border border-border p-2.5">
                      <Label className="text-xs">Status Akun Aktif</Label>
                      <Switch checked={eform.active} onCheckedChange={(v) => setEform({ ...eform, active: v })} data-testid="user-edit-active" />
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => setEditFor(null)}>Batal</Button>
            <Button onClick={submitEdit} disabled={busy} data-testid="user-edit-submit" variant="gold" size="sm" className="h-8 text-xs">Simpan Perubahan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
